import React, { useState, useEffect } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress"; // For loader
import { toast } from "react-toastify"; // For toast notifications
import Dialog from "@mui/material/Dialog"; // Modal component
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button"; // Button component
import styles from "./SpotifyAudioManager.module.css";
import { useAuth } from "../../../contexts/AuthContext";
import { smTypes } from "../../../util/flStates";
import { toQueryString } from "../../../util/utilFunctions";

function SpotifyAudioManager() {
  const { accessToken } = useAuth();
  const [podcasts, setPodcasts] = useState([]);
  const [embeds, setEmbeds] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [embedData, setEmbedData] = useState({ embedCode: "" });
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [openDialog, setOpenDialog] = useState(false); // Modal state
  const [selectedEmbedIndex, setSelectedEmbedIndex] = useState(null); // Selected embed for deletion

  // useEffect(() => {
  //   // Fetch existing podcast embeds from the backend
  //   const fetchEmbeds = async () => {
  //     try {
  //       const response = await axios.get("http://localhost:3000/podcasts", {
  //         headers: {
  //           Authorization: accessToken,
  //         },
  //       });
  //       const fetchedEmbeds = response.data.map((embed) => ({
  //         embedCode: convertSrcToEmbed(embed.src),
  //       }));
  //       setEmbeds(fetchedEmbeds);
  //     } catch (error) {
  //       console.error("Error fetching podcasts: ", error);
  //     }
  //   };

  //   fetchEmbeds();
  // }, [accessToken]);

  useEffect(() => {
    const fetchPodcasts = async (accessToken) => {
      try {
        const queryString = toQueryString({
          typeSMId: smTypes.spotifyPodcast,
        });

        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/list?${queryString}`,
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (response.status === 200 && response.data && response.data.success) {
          // console.log(response.data.smLinks);
          setPodcasts(response.data.smLinks);
        } else {
          const errorMsg =
            response.data.message || "Failed to fetch the social media links.";
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error while fetching all the Spotify Podcasts");
      }
    };

    if (accessToken) {
      fetchPodcasts(accessToken);
    }

    return () => {};
  }, [accessToken]);

  const handleInputChange = (e) => {
    setEmbedData({ ...embedData, [e.target.name]: e.target.value });
  };

  const extractSrcFromIframe = (iframeString) => {
    const dummyDiv = document.createElement("div");
    dummyDiv.innerHTML = iframeString;

    const iframeElement = dummyDiv.querySelector("iframe");
    return iframeElement ? iframeElement.src : null;
  };

  const convertSrcToEmbed = (src) => {
    return `<iframe style="border-radius:12px" src="${src}" width="100%" height="352" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>`;
  };

  const addPodcast = async () => {
    try {
      const src = extractSrcFromIframe(embedData.embedCode);

      if (src) {
        setIsLoading(true);
        setErrorMessage(""); // Reset error message

        const embedToSave = { embedCode: src }; // Save the src only
        try {
          if (editIndex !== null) {
            const updatedEmbeds = [...embeds];
            updatedEmbeds[editIndex] = embedToSave;
            setEmbeds(updatedEmbeds);
            setEditIndex(null);
            toast.success("Embed updated successfully!");
          } else {
            setEmbeds([...embeds, embedToSave]);
            toast.success("Embed added successfully!");
          }

          const requestData = {
            typeSMId: 4,
            url: src,
          };

          // Send src to backend
          await axios.post(
            `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/add`,
            requestData,
            {
              headers: {
                Authorization: accessToken,
              },
            }
          );
          setEmbedData({ embedCode: "" });

          if (
            response.status === 200 &&
            response.data &&
            response.data.success
          ) {
            // if (editIndex !== null) {
            //   const updatedPods = [...podcasts];
            //   updatedPods[editIndex] = embedData;
            //   setPodcasts(updatedReels);
            //   setEditIndex(null);
            //   toast.success("Reel updated successfully!");
            // } else {
            //   setReels([...reels, reelData]);
            //   toast.success("Reel added successfully!");
            // }
            // setReelData({ type: "Instagram", url: "" });
          } else {
            const errorMsg =
              response.data.message ||
              "Failed to upload the social media link.";
            setErrorMessage(errorMsg);
            toast.error(errorMsg);
          }
        } catch (error) {
          toast.error("Error saving podcast embed. Please try again!");
        } finally {
          setIsLoading(false); // Hide loader
        }
      } else {
        toast.error(
          "Invalid iframe code. Please provide a valid Spotify embed iframe."
        );
      }
    } catch (error) {
      console.error("Error occured while tryig to add a podcast: ", error);
    }
  };

  const addEmbed = async () => {
    const src = extractSrcFromIframe(embedData.embedCode);

    if (!src) {
      toast.error(
        "Invalid iframe code. Please provide a valid Spotify embed iframe."
      );
      return;
    }

    setIsLoading(true); // Show loader
    setErrorMessage(""); // Reset error message

    const requestData = {
      typeSMId: 4, // Spotify Embed
      url: src, // Save the extracted src
    };

    try {
      // Send the embed data to the backend
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/add`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        const newPodcast = {
          id: response.data.id,
          url: src,
          type_sm_id: 4,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          ...response.data.otherFields,
        };

        if (editIndex !== null) {
          // Update existing embed
          const updatedEmbeds = [...embeds];
          updatedEmbeds[editIndex] = { embedCode: src };
          setEmbeds(updatedEmbeds);
          setEditIndex(null);
          toast.success("Embed updated successfully!");
        } else {
          // Add new embed
          setEmbeds([...embeds, { embedCode: src }]);
          setPodcasts([...podcasts, newPodcast]);
          toast.success("Embed added successfully!");
        }

        setEmbedData({ embedCode: "" }); // Reset embed input
      } else {
        const errorMsg =
          response.data.message || "Failed to save the Spotify embed.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Error saving the Spotify embed. Please try again!";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false); // Hide loader
    }
  };

  const editEmbed = (index) => {
    setEditIndex(index);
    setEmbedData(embeds[index]);
  };

  // Open confirmation modal before deletion
  const handleDeleteClick = (index) => {
    setSelectedEmbedIndex(index);
    setOpenDialog(true);
  };

  // Close modal
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmbedIndex(null);
  };

  const confirmDelete = async () => {
    try {
      const embedToDelete = podcasts[selectedEmbedIndex].id; // Get the ID of the selected podcast
      // console.log("Deleting podcast with ID:", embedToDelete);

      // Send DELETE request to backend
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/delete/`,
        { id: embedToDelete }, // Pass the ID to delete
        {
          headers: {
            Authorization: accessToken, // Include authorization header
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        // Update the local state to remove the deleted podcast
        setPodcasts(podcasts.filter((podcast) => podcast.id !== embedToDelete));
        // console.log("Delete podcasts", podcasts);
        toast.success("Podcast deleted successfully!");
      } else {
        const errorMsg =
          response.data.message || "Failed to delete the podcast.";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error deleting podcast:", error);
      toast.error("Failed to delete the podcast. Please try again.");
    } finally {
      handleCloseDialog(); // Close the delete confirmation dialog
    }
  };

  // const getEmbedUrl = (url) => {
  //   if (url) {
  //     return `https://www.instagram.com/p/${url
  //       .split("/")
  //       .slice(-2, -1)}/embed/`;
  //   }
  //   return null;
  // };

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Manage Spotify Embeds</h3>

      <div className={styles.instructions}>
        <h4>How to Add Spotify Show Embed:</h4>
        <ul>
          <li>Go to Spotify and find the show you want to embed.</li>
          <li>Click the "Share" option and select "Embed Show."</li>
          <li>Copy the entire embed code and paste it into the field below.</li>
          <li>Click "Add" to embed it in your web page.</li>
        </ul>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <div className={styles.form}>
        <textarea
          name="embedCode"
          placeholder="Paste the Spotify embed iframe code here"
          value={embedData.embedCode}
          onChange={handleInputChange}
          className={styles.textarea}
        />
        <button
          onClick={addEmbed}
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : editIndex !== null ? (
            "Update"
          ) : (
            "Add"
          )}
        </button>
      </div>

      <div className={styles.embedsGrid}>
        {podcasts.map((podcast, index) => (
          <div key={index} className={styles.embedBox}>
            <iframe
              src={podcast.url}
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className={styles.embed}
            ></iframe>
            <div className={styles.actions}>
              <button
                onClick={() => editEmbed(index)}
                className={styles.editBtn}
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteClick(index)}
                className={styles.deleteBtn}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* <div className={styles.embedsGrid}>
        {embeds.map((embed, index) => (
          <div key={index} className={styles.embedBox}>
            <iframe
              src={embed.embedCode}
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className={styles.embed}
            ></iframe>
            <div className={styles.actions}>
              <button
                onClick={() => editEmbed(index)}
                className={styles.editBtn}
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteClick(index)}
                className={styles.deleteBtn}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div> */}

      {/* Modal for delete confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Spotify embed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SpotifyAudioManager;
